import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Spin } from 'antd';
import { SelectValue } from 'antd/lib/select';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { getCenterProfile, getZonesProfile } from '../service/MySrcmService';

const { Option } = Select;

interface OptionData {
	value?: string | number;
	label?: string;
}

export interface CenterOption {
	value: number | undefined;
	label: string | undefined;
}
interface RemoteSingleSelectProps {
	onChange?: (value: CenterOption) => void;
	value?: CenterOption | null | undefined;
	functionarySrcmId?: string;
	zoneValue?: string | number;
	disabled?: boolean;
}

const RemoteSingleSelect: React.FC<RemoteSingleSelectProps> = ({
	onChange,
	value,
	functionarySrcmId,
	zoneValue,
	disabled,
}: RemoteSingleSelectProps) => {
	const [options, setOptions] = useState<OptionData[]>([]);
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();

	const fetchData = useCallback(
		async (searchValue: string) => {
			setLoading(true);

			try {
				if (!functionarySrcmId || !zoneValue || typeof zoneValue !== 'string') {
					setOptions([]);
					return;
				}
				const zoneResponse = await getZonesProfile();

				const matchedZone = zoneResponse?.results?.find((zoneData) =>
					zoneData?.name?.toLowerCase().includes(zoneValue.toLowerCase())
				);

				if (!matchedZone?.id) {
					setOptions([]);
					return;
				}

				const centersPage = await getCenterProfile(matchedZone.id, searchValue);
				if (!centersPage?.results) {
					setOptions([]);
					return;
				}

				setOptions(
					centersPage.results.map((item) => ({
						value: item.id,
						label: item.name,
					}))
				);
			} catch {
				setOptions([]);
			} finally {
				setLoading(false);
			}
		},
		[functionarySrcmId, zoneValue]
	);

	useEffect(() => {
		void fetchData('');
	}, [fetchData]);

	const debouncedFetchData = useMemo(
		() => debounce(fetchData, 500),
		[fetchData]
	);

	const handleSearch = useCallback(
		(searchKey: SelectValue) => {
			void debouncedFetchData(searchKey as string);
		},
		[debouncedFetchData]
	);

	return (
		<Select
			showSearch
			defaultActiveFirstOption={false}
			showArrow={false}
			filterOption={false}
			placeholder={t('searchCenter')}
			notFoundContent={loading ? <Spin size="small" /> : null}
			onSearch={handleSearch}
			onChange={onChange}
			labelInValue
			value={value}
			disabled={disabled}
		>
			{options.map((option) => (
				// eslint-disable-next-line
				<Option key={option.value} value={option.value}>
					{option.label}
				</Option>
			))}
		</Select>
	);
};

export default RemoteSingleSelect;
